/* eslint-env browser */
import debounce from "lodash/debounce";
import { Controller } from "stimulus";
import { constrain } from "../../helpers/numbers";
import useStatusClasses from "../../composables/use_status_classes";
import useEventController from "../../composables/use_event_controller";

const ISSUE_TYPES = ["Issue::Missing", "Issue::Damaged", "Issue::IncorrectDelivery", "Issue::IncorrectPurchase"];

export default class extends Controller {
  static targets = [
    "checkbox",
    "checkmark",
    "formValue",
    "input",
    "invisibleLabel",
    "issues",
    "issueCount",
    "issueFlag",
    "issueIndicator",
    "resolvedIndicator",
    "resolvedCount",
    "issuesFormValue",
    "resolvedIssues",
    "resolvedIssuesFormValue",
    "resolvePopover",
    "resolvePopoverContent",
    "resolvedCounts",
  ];

  static values = {
    allocation: Number,
    default: 0,
    dirty: { type: Boolean, default: false },
    initialAllocation: Number,
    initialIssues: Object,
    issues: Object,
    initialResolvedIssues: Object,
    resolvedIssues: Object,
    lineItemId: Number,
    mainControl: Boolean,
    maxAllocation: Number,
    status: { type: String, default: "need_to_receive" },
  };

  static classes = ["issuesFlag", "resolvedFlag"];

  updateStatus = debounce(this.#updateStatus.bind(this), 10);
  flaggingType = "";
  popover = undefined;

  connect() {
    useEventController(this);
    useStatusClasses(this, "good");

    this.row = this.element.closest("[data-component=groupRow]");
    this.listenOn(document, `receivingRowToggleRow:${this.row.dataset.domId}`, this.#togglingRowCheckbox.bind(this));
    this.listenOn(document, "receivingReset", this.reset.bind(this));
    this.listenOn(document, "receivingRefresh", this.refresh.bind(this));
    this.listenOn(this.inputTarget, "blur", this.blurInput);

    this.updateStatusClasses(this.statusValue, undefined);
    this.popover = document.querySelector("#resolve-popover");

    // start listening for changes AFTER connecting to avoid unwanted initial value change calls
    Object.assign(this, {
      allocationValueChanged: this.#allocationValueChanged.bind(this),
      resolvedIssuesValueChanged: this.#resolvedIssuesValueChanged.bind(this),
    });

    this.updateDisplayedFlags();
  }

  #allocationValueChanged(value) {
    if (document.activeElement !== this.inputTarget) {
      this.inputTarget.value = constrain(value, this.totalFlagged, Infinity);
    }
    this.invisibleLabelTarget.textContent = this.inputTarget.value;

    this.checkDirtyStatus();

    if (this.hasFormValueTarget) {
      this.formValueTarget.name = this.dirtyValue
        ? `line_item_size[${this.element.dataset.lineItemId}][${this.element.dataset.size}]`
        : "";

      this.formValueTarget.value = this.inputTarget.value - this.totalFlagged;
    }

    this.updateStatus();

    if (this.mainControlValue) {
      this.dispatch("allocate", { prefix: "good" });
    }
  }

  initialAllocationValueChanged() {
    this.updateStatus();
  }

  issuesValueChanged() {
    this.updateDisplayedFlags();
    this.checkDirtyStatus();

    if (this.hasIssuesFormValueTarget) {
      this.issuesFormValueTarget.name = this.dirtyValue
        ? `line_item_issues[${this.element.dataset.lineItemId}][${this.element.dataset.size}]`
        : "";

      this.issuesFormValueTarget.value = JSON.stringify(this.issuesValue);
    }

    this.inputTarget.dataset.issues = this.totalFlagged;
  }

  #resolvedIssuesValueChanged() {
    this.updateDisplayedFlags();
    this.checkDirtyStatus();

    if (this.hasResolvedIssuesFormValueTarget) {
      this.resolvedIssuesFormValueTarget.name = this.dirtyValue
        ? `line_item_resolved[${this.element.dataset.lineItemId}][${this.element.dataset.size}]`
        : "";

      this.resolvedIssuesFormValueTarget.value = JSON.stringify(this.resolvedIssuesValue);
    }

    this.inputTarget.dataset.resolved = this.totalFlagged;

    if (this.mainControlValue) {
      this.dispatch("allocate", { prefix: "good" });
    }
  }

  statusValueChanged(value, previousValue) {
    this.element.dataset.status = value;
    this.checkboxTarget.classList.toggle("hidden", value === "received");

    if (this.hasCheckmarkTarget) {
      this.checkmarkTarget.classList.toggle("hidden", value !== "received");
    }

    this.updateStatusClasses?.(value, previousValue);
  }

  updateDisplayedFlags() {
    const unresolvedCount = Math.max(0, this.totalFlagged - this.resolvedCount);

    this.issueCountTarget.textContent = unresolvedCount;
    this.resolvedCountTarget.textContent = this.resolvedCount;

    this.issuesTarget.classList.toggle("hidden", this.totalFlagged === 0);
    if (this.totalFlagged !== 0) {
      this.issueIndicatorTarget.classList.toggle("hidden", unresolvedCount === 0);
      this.resolvedIndicatorTarget.classList.toggle("hidden", this.resolvedCount === 0);
    }
  }

  blurInput() {
    this.inputTarget.value = this.allocationValue;
  }

  input(event) {
    const updatedValue = parseInt(event.target.value.replace(/\D/g, "") || 0, 10);

    if (this.flaggingEnabled) {
      const minimumValue = this.allocationValue - (this.issuesValue[this.flaggingType] ?? 0);

      if (updatedValue >= minimumValue) {
        const change = updatedValue - this.allocationValue;
        this.syncIssueData(...this.setIssue(change));
      }
    } else {
      this.allocationValue = constrain(updatedValue, this.totalFlagged, Infinity);
      this.inputTarget.value = this.allocationValue;
      this.syncDependentFields();
    }
  }

  changeBy(amount) {
    if (this.flaggingEnabled) {
      if ((!this.issuesValue[this.flaggingType] || !this.allocationValue) && amount < 0) return;

      this.syncIssueData(...this.setIssue(amount));
    } else {
      this.allocationValue = constrain(this.allocationValue + amount, this.totalFlagged, Infinity);
      this.syncDependentFields();
    }
  }

  increment() {
    this.changeBy(1);
  }

  decrement() {
    this.changeBy(-1);
  }

  flaggingModeUpdated({ detail }) {
    this.flaggingType = detail.type ?? "";
    this.checkboxTarget.disabled = !!detail.type;
  }

  refresh() {
    this.initialIssuesValue = this.issuesValue;
    this.initialResolvedIssuesValue = this.resolvedIssuesValue;
    this.initialAllocationValue = this.allocationValue;
    this.uncheck();
  }

  reset() {
    this.issuesValue = this.initialIssuesValue;
    this.resolvedIssuesValue = this.initialResolvedIssuesValue;
    this.allocationValue = this.initialAllocationValue;
    this.uncheck();
  }

  check() {
    if (this.flaggingEnabled) {
      if (this.checkboxTarget.checked) {
        this.syncIssueData(...this.setIssue(this.maxAllocationValue - this.allocationValue));
      } else {
        if (!this.issuesValue[this.flaggingType] || !this.allocationValue) return;
        this.syncIssueData(...this.setIssue(-1 * (this.issuesValue[this.flaggingType] || 0)));
      }
    } else if (!this.checkboxTarget.checked) {
      this.issuesValue = this.initialIssuesValue;
      this.resolvedIssuesValue = this.initialResolvedIssuesValue;
      this.allocationValue = this.initialAllocationValue;
      this.uncheck();
      this.syncResetFields();
    } else {
      if (!this.fullyAllocated) {
        this.allocationValue = this.maxAllocationValue;
      }

      this.syncDependentFields();
    }

    this.updateStatus();
    this.dispatch("update", { prefix: "good" });
  }

  uncheck() {
    this.checkboxTarget.indeterminate = false;
    this.checkboxTarget.checked = false;
    this.updateStatus();
    this.dispatch("update", { prefix: "good" });
  }

  #updateStatus() {
    if (this.checkboxTarget.checked) {
      this.statusValue = "need_to_receive";
    } else if (this.fullyAllocated && this.issuesResolved) {
      this.statusValue = "received";
    } else if (this.allocationValue) {
      this.statusValue = "partially_received";
    } else {
      this.statusValue = "need_to_receive";
    }
  }

  checkDirtyStatus() {
    this.dirtyValue = this.dirtyAllocationCount + this.dirtyIssuesCount + this.dirtyResolvedCount !== 0;
    this.checkboxTarget.checked = this.dirtyValue;
    this.checkboxTarget.indeterminate = this.checkboxTarget.checked && (!this.fullyAllocated || !this.issuesResolved);
    this.dispatch("update", { prefix: "good" });
  }

  setIssue(change) {
    this.allocationValue += change;

    const previousValue = this.issuesValue[this.flaggingType] ?? 0;
    const newValue = previousValue + change;
    this.issuesValue = { ...this.issuesValue, [this.flaggingType]: newValue };
    const resolveIssueType = this.#resolvedIssueType(this.flaggingType);
    if (resolveIssueType in this.resolvedIssuesValue) {
      const constrainedCount = constrain(this.resolvedIssuesValue[resolveIssueType], 0, newValue);
      const resolvedChange = this.resolvedIssuesValue[resolveIssueType] - constrainedCount;

      // finish updating issue count before syncing the resolved counts
      setTimeout(() => {
        this.syncResolvedIssueData(this.flaggingType, ...this.setResolvedIssue(resolvedChange, this.flaggingType));
      });
    }

    return [newValue, previousValue];
  }

  setResolvedIssue(change, issueType) {
    const maxIssueCount = this.issuesValue[issueType] ?? 0;
    const resolveIssueType = this.#resolvedIssueType(issueType);
    const previousValue = this.resolvedIssuesValue[resolveIssueType] ?? 0;
    const value = constrain(previousValue + change, 0, maxIssueCount);

    this.resolvedIssuesValue = {
      ...this.resolvedIssuesValue,
      [resolveIssueType]: value,
    };

    return [this.resolvedIssuesValue[resolveIssueType], previousValue];
  }

  syncDependentFields() {
    if (this.mainControlValue) {
      // Distribute allocation across dependent fields.
      const fields = this.dependentFields.slice(1);

      const leftover = fields.reduce((value, controller) => {
        const valueAndFlagged = value + controller.totalFlagged;
        controller.allocationValue = constrain(valueAndFlagged, controller.totalFlagged, controller.maxAllocationValue);
        return valueAndFlagged - controller.allocationValue;
      }, this.allocationValue - this.totalFlagged);

      // If anything is left over after distribution, add it to the first dependent field.
      fields[0].allocationValue += Math.max(0, leftover);
    } else {
      this.dependentFields[0].allocationValue = this.dependentFields
        .slice(1)
        .reduce((total, field) => total + field.allocationValue, 0);
    }
  }

  syncResetFields() {
    if (this.mainControlValue) {
      this.dependentFields.slice(1).forEach((field) => {
        field.reset();
      });
    } else {
      this.dependentFields[0].allocationValue = this.dependentFields
        .slice(1)
        .reduce((total, field) => total + field.allocationValue, 0);
    }
  }

  incrementResolved({ params: { issueType } }) {
    this.syncResolvedIssueData(issueType, ...this.setResolvedIssue(1, issueType));
    this.syncResolvedPopover();
  }

  decrementResolved({ params: { issueType } }) {
    this.syncResolvedIssueData(issueType, ...this.setResolvedIssue(-1, issueType));
    this.syncResolvedPopover();
  }

  configureResolvedPopover() {
    this.resolvePopoverContentTarget.appendChild(this.popover);

    this.syncResolvedPopover();
    this.positionFixedResolvePopover();
  }

  syncResolvedPopover() {
    ISSUE_TYPES.forEach((type) => {
      const rowType = this.popover.querySelector(`[data-resolve-type="${type}"]`);
      const value = this.issuesValue[type] ?? 0;
      rowType.classList.toggle("hidden", value === 0);

      const counts = rowType.querySelector("[data-component=resolveCount]");
      const resolved = this.resolvedIssuesValue[this.#resolvedIssueType(type)] ?? 0;
      counts.textContent = `${resolved} / ${value}`;

      const issuesIcon = rowType.querySelector("[data-component=issuesIcon]");
      const resolvedIcon = rowType.querySelector("[data-component=resolvedIcon]");
      issuesIcon.classList.toggle("hidden", resolved >= value);
      resolvedIcon.classList.toggle("hidden", resolved < value);

      const typeName = rowType.querySelector("[data-component=typeName]");
      typeName.classList.toggle(this.issuesFlagClass, resolved < value);
      typeName.classList.toggle(this.resolvedFlagClass, resolved >= value);
    });
  }

  positionFixedResolvePopover() {
    const popover = this.resolvePopoverTarget;
    popover.style.position = ``;
    popover.style.left = ``;
    popover.style.top = ``;
    popover.style.bottom = ``;

    setTimeout(() => {
      const popoverRect = this.issueFlagTarget.getBoundingClientRect();
      const { scrollTop, scrollLeft } = document.querySelector(".page-container");
      popover.style.zIndex = 1000;
      popover.style.position = "fixed";
      popover.style.left = `${popoverRect.left + scrollLeft}px`;
      popover.style.top = `${popoverRect.top - popover.offsetHeight + scrollTop}px`;
    });
  }

  syncIssueData(newValue, previousValue) {
    if (this.mainControlValue) {
      let totalFlagged = newValue;

      this.dependentFields
        .slice(1)
        .toReversed()
        .forEach((field) => {
          const currentFlagged = field.issuesValue[this.flaggingType] || 0;
          const newFlagged = Math.min(
            totalFlagged,
            field.maxAllocationValue - (field.allocationValue - currentFlagged),
          );

          field.issuesValue = { ...field.issuesValue, [this.flaggingType]: newFlagged };
          field.allocationValue = field.allocationValue - currentFlagged + newFlagged;

          totalFlagged -= newFlagged;
        });

      if (totalFlagged > 0) {
        this.dependentFields.at(-1).setIssue(totalFlagged);
      }
    } else {
      this.dependentFields[0].setIssue(newValue - previousValue);
    }
  }

  syncResolvedIssueData(issueType, value, previousValue) {
    if (this.mainControlValue) {
      const resolvedIssueType = this.#resolvedIssueType(issueType);
      let totalResolved = this.resolvedIssuesValue[resolvedIssueType];

      this.dependentFields.slice(1).forEach((field) => {
        const issueCount = field.issuesValue[issueType] || 0;
        if (issueCount === 0) return;

        const resolvedCount = Math.min(totalResolved, issueCount);
        const change = resolvedCount - (field.resolvedIssuesValue[resolvedIssueType] || 0);
        field.setResolvedIssue(change, issueType);

        totalResolved -= resolvedCount;
      });
    } else {
      const change = value - previousValue;
      if (!change) return;

      this.dependentFields[0].setResolvedIssue(change, issueType);
    }
  }

  #togglingRowCheckbox(e) {
    const { checked } = e.detail;

    if (!checked || !this.fullyAllocated) {
      this.checkboxTarget.indeterminate = false;
      this.checkboxTarget.checked = checked;
      this.check();
    }
  }

  #resolvedIssueType(issueType) {
    return `${issueType}Resolved`;
  }

  #issueChangeCount(issueType) {
    const issuesCount = this.issuesValue[issueType] || 0;
    const initialIssuesCount = this.initialIssuesValue[issueType] || 0;

    return issuesCount - initialIssuesCount;
  }

  #resolveChangeCount(issueType) {
    const resolvedType = this.#resolvedIssueType(issueType);
    const resolvedCount = this.resolvedIssuesValue[resolvedType] || 0;
    const initialCount = this.initialResolvedIssuesValue[resolvedType] || 0;

    return resolvedCount - initialCount;
  }

  get dependentFields() {
    return [
      ...this.row.querySelectorAll(`[data-component=receivableGood][data-size=${this.element.dataset.size}]`),
    ].map((element) => this.application.getControllerForElementAndIdentifier(element, "goods--receivable-good"));
  }

  get dirtyAllocationCount() {
    return Math.abs(this.allocationValue - this.initialAllocationValue);
  }

  get dirtyIssuesCount() {
    return Math.abs(this.totalFlagged - this.initialFlagged);
  }

  get dirtyResolvedCount() {
    return Object.keys(this.issuesValue).reduce(
      (total, issueType) => total + Math.abs(this.#resolveChangeCount(issueType)),
      0,
    );
  }

  get isDirty() {
    return this.dirtyAllocationCount + this.dirtyIssuesCount + this.dirtyResolvedCount !== 0;
  }

  get countChanges() {
    let allocationChanges = this.allocationValue - this.initialAllocationValue;
    let issueChanges = 0;
    let resolvedChanges = 0;

    Object.keys(this.issuesValue).forEach((issueType) => {
      const issueChangeCount = Math.max(0, this.#issueChangeCount(issueType));
      const resolvingIssueChanges = this.#resolveChangeCount(issueType);
      const unresolvingChanges = -Math.min(0, resolvingIssueChanges);
      const resolvingChanges = Math.max(0, resolvingIssueChanges);
      const changesInIssues = Math.max(0, issueChangeCount + unresolvingChanges - resolvingChanges);

      issueChanges += changesInIssues;
      resolvedChanges += resolvingChanges;

      // allocationChanges already includes issueChangeCount so let's not count that again
      allocationChanges += changesInIssues + resolvingChanges - issueChangeCount;
    }, 0);

    return {
      allocationChanges,
      issueChanges,
      resolvedChanges,
    };
  }

  get issuesResolved() {
    if (this.totalFlagged === 0) return true;

    return !Object.entries(this.issuesValue).find(([key, value]) => {
      const resolvedCount = this.resolvedIssuesValue[this.#resolvedIssueType(key)] || 0;
      return (value || 0) > resolvedCount;
    });
  }

  get resolvedCount() {
    if (this.totalFlagged === 0) return 0;

    return Object.keys(this.issuesValue).reduce((total, key) => {
      const resolvedCount = this.resolvedIssuesValue[this.#resolvedIssueType(key)] || 0;
      return total + resolvedCount;
    }, 0);
  }

  get flaggingEnabled() {
    return this.flaggingType !== "";
  }

  get fullyAllocated() {
    return this.allocationValue >= this.maxAllocationValue;
  }

  get initialFlagged() {
    return Object.values(this.initialIssuesValue).reduce((total, current) => total + current, 0);
  }

  get totalFlagged() {
    return Object.values(this.issuesValue).reduce((total, current) => total + current, 0);
  }
}
